import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  useAiSummary,
  useRegenerateAiSummary,
  type AiSummaryStatus,
} from "../../../helpers/hooks/use-ai-summary";

interface Props {
  customerId: string;
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusBadge({ status }: { status: AiSummaryStatus }) {
  const cls =
    status === "fresh"
      ? "bg-green-50 text-green-700 border-green-200"
      : status === "generating"
        ? "bg-blue-50 text-blue-700 border-blue-200"
        : status === "error"
          ? "bg-red-50 text-red-700 border-red-200"
          : "bg-yellow-50 text-yellow-700 border-yellow-200";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${cls}`}
    >
      {status === "generating" ? "generating…" : status}
    </span>
  );
}

/**
 * AI-generated 3-paragraph customer narrative. Fetches on mount, polls every
 * 3s while status === 'generating' (cap 60s), renders markdown. Regenerate
 * button forces a fresh model call bypassing the 24h cache.
 */
export function AiSummarySection({ customerId }: Props) {
  const { data, isLoading, isError, refetch } = useAiSummary(customerId);
  const regenerate = useRegenerateAiSummary(customerId);

  const isGenerating = data?.status === "generating";

  const headerMeta: string[] = [];
  if (data?.model) headerMeta.push(data.model);
  if (data?.computedAt) headerMeta.push(formatTimestamp(data.computedAt));

  return (
    <section className="bg-surface-primary rounded-xl border border-border-secondary shadow-sm">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border-secondary">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-content-primary">
            AI Summary
          </h2>
          {data?.status && <StatusBadge status={data.status} />}
        </div>
        <button
          onClick={() => regenerate.mutate()}
          disabled={isGenerating || regenerate.isPending}
          className="text-xs font-medium text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGenerating || regenerate.isPending ? "Regenerating…" : "Regenerate"}
        </button>
      </div>

      <div className="px-6 py-4">
        {isLoading && !data && (
          <div className="text-sm text-content-tertiary">Loading…</div>
        )}

        {isError && !data && (
          <div className="text-sm text-red-600 flex items-center gap-3">
            <span>Failed to load summary.</span>
            <button
              onClick={() => refetch()}
              className="text-primary hover:underline"
            >
              Retry
            </button>
          </div>
        )}

        {isGenerating && (
          <div className="flex items-center gap-2 text-sm text-content-tertiary">
            <span className="inline-block w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span>Generating the narrative…</span>
          </div>
        )}

        {data?.status === "error" && (
          <div className="text-sm text-red-600">
            <div className="font-medium">Generation failed</div>
            {data.lastError && (
              <div className="mt-1 text-xs text-red-500 break-words">
                {data.lastError}
              </div>
            )}
          </div>
        )}

        {data?.summaryText && data.status !== "generating" && (
          <div className="prose prose-sm max-w-none text-content-primary">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {data.summaryText}
            </ReactMarkdown>
          </div>
        )}

        {!isLoading && !isError && !data?.summaryText && !isGenerating && (
          <div className="text-sm text-content-tertiary italic">
            No summary yet — click <strong>Regenerate</strong> to generate one.
          </div>
        )}
      </div>

      {headerMeta.length > 0 && data?.summaryText && (
        <div className="px-6 py-2 border-t border-border-secondary text-[10px] text-content-tertiary">
          {headerMeta.join(" · ")}
        </div>
      )}
    </section>
  );
}
